/** Master BRD v3.0 display contract. Does not rename G1–G8. */

export const JOURNEY = "WHY → DECIDE → DELIVER → PROVE";
/** Legacy BRD v3 pillar labels; public journey is JOURNEY above. */
export const BRD_OVERLAY_JOURNEY = JOURNEY;

export const DECISION_ROLES = new Set([
  "Enterprise Architect",
  "Solution Architect",
  "Product Owner",
  "Business Owner",
  "Modernization Lead",
  "Security Architect",
]);

export const DISCLOSURE_RANK = { l1: 1, l2: 2, l3: 3, l4: 4 };

export const ROUTE_DEPTH = {
  "/": 1,
  "/overview": 2,
  "/delivery": 2,
  "/applications": 3,
  "/program": 3,
  "/lifecycle": 3,
  "/assessment": 3,
  "/cockpit": 3,
  "/decision": 3,
  "/enterprise": 3,
  "/governance": 3,
  "/evidence": 4,
};

export function fillVoice(text, count) {
  return String(text || "").replaceAll("{count}", String(count ?? ""));
}

export function canAuthorize(role) {
  return DECISION_ROLES.has(role);
}

export function disclosureRank(level) {
  return DISCLOSURE_RANK[level] || 2;
}

export function maxDisclosureOf(guideOrUser) {
  return guideOrUser?.max_disclosure || guideOrUser?.guide?.max_disclosure || "l4";
}

export function routePath(href) {
  return String(href || "/").split("?")[0] || "/";
}

export function routeAllowed(href, maxLevel) {
  const need = ROUTE_DEPTH[routePath(href)] ?? 3;
  return need <= disclosureRank(maxLevel);
}

export function navItemAllowed(href, guide) {
  if (!guide?.role) return true;
  const maxLevel = maxDisclosureOf(guide);
  if (!routeAllowed(href, maxLevel)) return false;
  const nav = guide.nav;
  if (!Array.isArray(nav)) return true;
  return nav.includes(routePath(href));
}

const APP_OPEN_ORDER = ["/cockpit", "/decision", "/lifecycle", "/governance", "/assessment", "/delivery", "/"];

export function appOpenRoute(guide) {
  return APP_OPEN_ORDER.find((href) => navItemAllowed(href, guide)) || "/";
}

export function canWriteEvidence(guide) {
  if (!guide?.role || guide.read_only) return false;
  return disclosureRank(maxDisclosureOf(guide)) >= 4;
}

export function canWriteProgram(guide) {
  if (!guide?.role || guide.read_only) return false;
  return disclosureRank(maxDisclosureOf(guide)) >= 3;
}

export function canCreateApp(guide) {
  if (!guide?.role || guide.read_only) return false;
  return [
    "Product Owner",
    "App Owner",
    "Tenant Admin",
    "Modernization Lead",
    "Program Manager",
    "Enterprise Architect",
  ].includes(guide.role);
}

export function canCreateProgram(guide) {
  if (!guide?.role || guide.read_only) return false;
  return ["Product Owner", "Program Manager", "Tenant Admin"].includes(guide.role);
}

export function canSignProgramCharter(guide) {
  if (!guide?.role || guide.read_only) return false;
  return ["Product Owner", "CIO", "Executive Sponsor"].includes(guide.role);
}

export function canSetProgramFunding(guide) {
  if (!guide?.role || guide.read_only) return false;
  return ["CIO", "Product Owner", "Tenant Admin"].includes(guide.role);
}

export function canApproveFundedRoadmap(guide) {
  if (!guide?.role || guide.read_only) return false;
  return ["CIO", "Product Owner", "Executive Sponsor", "Tenant Admin"].includes(guide.role);
}

export function canWriteWhy(guide) {
  if (!guide?.role || guide.read_only) return false;
  return disclosureRank(maxDisclosureOf(guide)) >= 3;
}

export function canSignGates(guideOrRole) {
  const role = typeof guideOrRole === "string" ? guideOrRole : guideOrRole?.role;
  const readOnly = typeof guideOrRole === "object" && guideOrRole?.read_only;
  return canAuthorize(role) && !readOnly;
}

/** L1–L2 roles get a queue-first home without StorySpine chrome. */
export function showStorySpine(guide) {
  return disclosureRank(maxDisclosureOf(guide)) >= 3;
}

/** Back/Next pager only for L4 decision authorities reviewing evidence depth. */
export function storySpinePager(guide) {
  return disclosureRank(maxDisclosureOf(guide)) >= 4;
}

export function roleGateFocus(role) {
  const map = {
    "Product Owner": "G2",
    "Enterprise Architect": "G3",
    "Modernization Lead": "G4",
    "Security Architect": "G5",
    "Solution Architect": "G6",
    "Business Owner": "G7",
  };
  return map[role] || null;
}

export const PROGRAM_CORE_MODULES = ["overview", "dependencies", "waves", "raid"];

export function programModulesForRole(role) {
  if (role === "Program Manager") return PROGRAM_CORE_MODULES;
  return null;
}

export function defaultDisclosure(role) {
  if (role === "App Owner" || role === "Auditor" || role === "CIO" || role === "Executive Sponsor") return "l1";
  if (
    role === "Enterprise Architect"
    || role === "Solution Architect"
    || role === "Security Architect"
    || role === "Program Manager"
    || role === "Modernization Lead"
  ) return "l3";
  return "l2";
}

export const PILLAR_NAV = [
  {
    id: "decide",
    label: "DECIDE",
    items: [
      ["Analytics", "/", "LayoutDashboard"],
      ["Applications", "/applications", "Building2"],
      ["Application", "/overview", "Gauge"],
      ["Assessment Inputs", "/assessment", "ClipboardList"],
      ["Evidence Intake", "/evidence", "Upload"],
      ["Decision Cockpit", "/cockpit", "Sparkles"],
      ["7R Decision", "/decision", "Gauge"],
      ["Enterprise Fit", "/enterprise", "Network"],
    ],
  },
  {
    id: "deliver",
    label: "DELIVER",
    items: [
      ["Program", "/program", "Flag"],
      ["Lifecycle", "/lifecycle", "RouteIcon"],
      ["G1–G8 Governance", "/governance", "ShieldCheck"],
    ],
  },
  {
    id: "value",
    label: "VALUE",
    items: [
      ["Delivery & Value", "/delivery", "GitBranch"],
    ],
  },
];

export const CRUMB_PAGE = {
  "/": "Analytics",
  "/overview": "Application",
  "/applications": "Applications",
  "/program": "Program",
  "/lifecycle": "Lifecycle",
  "/assessment": "Assessment",
  "/evidence": "Evidence",
  "/cockpit": "Cockpit",
  "/decision": "Decision",
  "/governance": "Governance",
  "/delivery": "Value",
  "/enterprise": "Architecture",
};
